import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { UserInfo, TenantInfo, LoginResponse } from '../types';
import { getApiBaseUrl } from '../utils/apiConfig';

interface AuthContextType {
  user: UserInfo | null;
  token: string | null;
  tenant: TenantInfo | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * SECURITY: Check if JWT token is expired
 * Returns true if token is expired or invalid
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false; // No expiration claim

    // Check if expired (with 30 second buffer)
    return payload.exp * 1000 < Date.now() - 30000;
  } catch {
    return true; // Invalid token format
  }
};

/**
 * SECURITY: Safely parse JSON from localStorage
 * Returns null if parsing fails or data is invalid
 */
const safeJsonParse = <T,>(data: string | null, validator?: (obj: any) => boolean): T | null => {
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    if (validator && !validator(parsed)) return null;
    return parsed as T;
  } catch {
    return null;
  }
};

/**
 * SECURITY: Validate user object has required fields
 */
const isValidUser = (obj: any): boolean => {
  return (
    obj &&
    typeof obj === 'object' &&
    (typeof obj.userId === 'number' || typeof obj.userId === 'string') &&
    typeof obj.username === 'string'
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('mdm_auth_token');
    const storedUser = localStorage.getItem('mdm_user');
    const storedTenant = localStorage.getItem('mdm_tenant');

    // SECURITY: Validate token is not expired before using
    if (storedToken && !isTokenExpired(storedToken) && storedUser) {
      // SECURITY: Safely parse user data with validation
      const parsedUser = safeJsonParse<UserInfo>(storedUser, isValidUser);

      if (parsedUser) {
        setToken(storedToken);
        setUser(parsedUser);

        // Load tenant info if available (safely parsed)
        if (storedTenant) {
          const parsedTenant = safeJsonParse<TenantInfo>(storedTenant);
          if (parsedTenant) {
            setTenant(parsedTenant);
          }
        }

        // Set axios default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } else {
        // Invalid user data - clear storage
        localStorage.removeItem('mdm_auth_token');
        localStorage.removeItem('mdm_user');
        localStorage.removeItem('mdm_tenant');
      }
    } else if (storedToken && isTokenExpired(storedToken)) {
      // SECURITY: Token expired - clear all auth data
      localStorage.removeItem('mdm_auth_token');
      localStorage.removeItem('mdm_user');
      localStorage.removeItem('mdm_tenant');
      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Token expired, cleared auth data');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    try {
      // Get API base URL with /api suffix from centralized config
      const apiBaseUrl = getApiBaseUrl();

      // Detect subdomain and include in request headers
      const host = window.location.hostname;
      const parts = host.split('.');
      const subdomain =
        parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www' ? parts[0] : null;

      const headers: any = {};
      if (subdomain) {
        headers['X-Tenant-Subdomain'] = subdomain;
        console.log(`Login request with subdomain: ${subdomain}`);
      }

      const response = await axios.post<LoginResponse>(
        `${apiBaseUrl}/Auth/login`,
        {
          username,
          password,
        },
        { headers }
      );

      const data = response.data;

      if (!data.success || !data.token || !data.user) {
        throw new Error(data.error || 'Invalid username or password');
      }

      // Store in localStorage
      localStorage.setItem('mdm_auth_token', data.token);
      localStorage.setItem('mdm_user', JSON.stringify(data.user));

      // Store tenant information if present
      if (data.tenant) {
        localStorage.setItem('mdm_tenant', JSON.stringify(data.tenant));
        setTenant(data.tenant);
      } else {
        // SuperAdmin users don't have tenant info
        localStorage.removeItem('mdm_tenant');
        setTenant(null);
      }

      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

      setToken(data.token);
      setUser(data.user);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(
        error.response?.data?.error || error.message || 'Invalid username or password'
      );
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('mdm_auth_token');
    localStorage.removeItem('mdm_user');
    localStorage.removeItem('mdm_tenant');

    // Clear axios default authorization header
    delete axios.defaults.headers.common['Authorization'];

    setToken(null);
    setUser(null);
    setTenant(null);
  };

  const value: AuthContextType = {
    user,
    token,
    tenant,
    login,
    logout,
    isAuthenticated: !!token,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
